import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for scroll-based section reveals
 * 
 * Uses Intersection Observer to trigger animations when section enters viewport.
 * Triggers at 75% viewport entry.
 * One-time animation (no re-trigger on scroll up).
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Intersection threshold (default: 0.75)
 * @param {string} options.rootMargin - Root margin for intersection (default: '0px')
 * @returns {Object} - { ref, isVisible }
 */
export const useScrollReveal = (options = {}) => {
  const { threshold = 0.75, rootMargin = '0px' } = options;
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only trigger once - no re-trigger on scroll up
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsVisible(true);
          hasAnimated.current = true;
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [threshold, rootMargin]);

  return { ref, isVisible };
};

